import React from "react";
import { Lightbulb, Clock, AppWindow, Activity, ArrowRight } from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { Drawer } from "../../components/Drawer";
import { fmtH } from "./brainSignals.mjs";

/* Slide-in explainer for the four orbit modules (INS/SUM/APP/PAT).
   What the lobe is, live bullets from data the brain page already holds
   (no extra fetches), and one button through to the full screen. */

const MONO = "'JetBrains Mono','SF Mono',monospace";

function Bullet({ t, tone, children }) {
  return (
    <li style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "7px 0", fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.5 }}>
      <span style={{ width: 6, height: 6, borderRadius: 99, marginTop: 7, flexShrink: 0, background: tone || t.c.primary }} />
      <span style={{ minWidth: 0 }}>{children}</span>
    </li>
  );
}

/* Per-module content: blurb + live bullets, all derived from `d`. */
function buildContent(mod, d, t) {
  const anomalies = d.insightEntries.filter((e) => e.type === "anomaly");
  const growth = d.insightEntries.filter((e) => e.type === "productive");
  const topPeople = [...d.members].sort((a, b) => (b.totalMinutes || 0) - (a.totalMinutes || 0)).slice(0, 3);
  const silent = d.members.filter((m) => !m.hasData).length;

  switch (mod.key) {
    case "org-intelligence":
      return {
        icon: <Lightbulb size={18} />,
        blurb: "AI-written observations about your org — growth signals worth amplifying and patterns worth a closer look, generated from daily work summaries.",
        bullets: [
          { tone: t.status.positive, text: `${growth.length} growth signal${growth.length === 1 ? "" : "s"} in this period` },
          { tone: anomalies.length ? t.status.warning : t.status.positive, text: `${anomalies.length} anomal${anomalies.length === 1 ? "y" : "ies"} flagged` },
          ...growth.slice(0, 2).map((e) => ({
            tone: t.status.positive,
            text: `${e.user?.name || "Someone"}: ${String(e.content || "").slice(0, 110)}${String(e.content || "").length > 110 ? "…" : ""}`,
          })),
        ],
        cta: "Open Insights",
      };
    case "work-summary":
      return {
        icon: <Clock size={18} />,
        blurb: "Tracked time across the team for the selected period — who worked, on what, and where the idle time sits.",
        bullets: [
          { text: <>Team tracked <b style={{ fontFamily: MONO }}>{fmtH(d.kpi?.totalMinutes || 0)}</b> across {d.kpi?.totalSessions || 0} sessions</> },
          { tone: t.status.positive, text: `${d.kpi?.activeMembers || 0} of ${d.kpi?.teamSize || 0} members active` },
          { tone: (d.kpi?.idleMinutes || 0) > 0 ? t.status.warning : undefined, text: <>Idle drag: <b style={{ fontFamily: MONO }}>{fmtH(d.kpi?.idleMinutes || 0)}</b></> },
          ...(topPeople.length && topPeople[0].totalMinutes > 0
            ? [{ text: `Most tracked: ${topPeople.filter((p) => p.totalMinutes > 0).map((p) => `${p.name.split(" ")[0]} (${fmtH(p.totalMinutes)})`).join(", ")}` }]
            : []),
        ],
        cta: "Open Work Summary",
      };
    case "app-usage":
      return {
        icon: <AppWindow size={18} />,
        blurb: "Which tools and websites the team actually spends time in — per person, per app, with productive vs distracting classification.",
        bullets: [
          { text: "Top apps and sites ranked by time spent" },
          { text: "Per-person breakdown: tools each member lives in" },
          { tone: t.status.warning, text: "Distraction share, so app time turns into a signal not a log" },
        ],
        cta: "Open App Usage",
      };
    case "patterns":
      return {
        icon: <Activity size={18} />,
        blurb: "The brain's attention list — everything it currently thinks needs a manager's eye, ranked by impact in hours.",
        bullets: [
          { tone: d.moves.length ? t.status.warning : t.status.positive, text: `${d.moves.length} open move${d.moves.length === 1 ? "" : "s"} in the queue` },
          ...(silent > 0 ? [{ tone: t.status.negative, text: `${silent} member${silent === 1 ? "" : "s"} with no tracked time` }] : []),
          ...d.moves.slice(0, 3).map((mv) => ({
            tone: mv.severity === "critical" ? t.status.negative : mv.severity === "growth" ? t.status.positive : t.status.warning,
            text: mv.title,
          })),
        ],
        cta: "See the full list",
      };
    default:
      return null;
  }
}

export function ModulePanel({ open, module: mod, d, onOpenScreen, onClose }) {
  const t = useDashTheme();
  const content = mod ? buildContent(mod, d, t) : null;
  return (
    <Drawer
      open={open && !!content}
      onClose={onClose}
      title={mod?.label || ""}
      subtitle={mod?.sub}
      icon={content?.icon}
      iconTone="ai"
      width={420}
      footer={content && (
        <button
          onClick={() => { onClose(); onOpenScreen(mod); }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            height: 38, padding: "0 18px", border: "none", borderRadius: 10,
            background: t.c.primary, color: "#fff",
            fontSize: t.font.size.sm, fontWeight: 600, fontFamily: t.font.family, cursor: "pointer",
          }}
        >
          {content.cta} <ArrowRight size={15} />
        </button>
      )}
    >
      {content && (
        <>
          <p style={{ margin: 0, fontSize: t.font.size.sm, color: t.c.text, lineHeight: 1.6 }}>{content.blurb}</p>
          <div style={{ margin: `${t.s[4]}px 0 ${t.s[2]}px`, fontSize: t.font.size.xs, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: t.c.textTertiary }}>
            Right now
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {content.bullets.map((b, i) => <Bullet key={i} t={t} tone={b.tone}>{b.text}</Bullet>)}
          </ul>
        </>
      )}
    </Drawer>
  );
}
